/**
 * Batch 18-J15 — Provider User Mapping Admin Panel
 *
 * Operator surface that maps a provider's external user IDs /
 * emails to existing OptiBay technician profiles / users. Renders
 * inside `TrainingCatalogAdmin` under the `Provider User Mapping`
 * tab. Backed by the J3 read RPCs + the J6 user-mapping upsert RPC.
 *
 * Scope:
 *   - provider selector via `list_training_providers_for_admin` (J3)
 *   - per-provider user mapping list via
 *     `list_training_provider_user_mappings_for_admin` (J3) —
 *     returns `masked_external_user_email`, never raw full email
 *   - create / edit user mapping via
 *     `upsert_training_provider_user_mapping` (J6)
 *   - supporting unmatched-events summary via
 *     `list_training_provider_unmatched_events_for_admin` (J3)
 *
 * Out of scope (deferred to later J-lane batches):
 *   - unresolved events full triage UI
 *   - audit history UI
 *   - provider registry CRUD
 *   - event reprocess action ("save and reprocess")
 *   - signed JWT key management
 *
 * Trust boundaries:
 *   - User mapping is identity routing/normalization. It does NOT
 *     verify completion, create attestation, create evidence
 *     events, or create manager verification.
 *   - The panel never reads provider tables directly; J3/J6 RPCs
 *     are the only seams.
 *   - The list/read RPC returns masked email only. The dialog
 *     accepts a raw email INPUT (user-entered) but the panel never
 *     re-displays it after save — only the masked form returned by
 *     the J3 RPC is rendered.
 *   - Raw provider event payload, payload hash, signature status,
 *     idempotency key, HMAC secret material are never displayed.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Loader2, Pencil, Plus, Search } from 'lucide-react';

// ─── Narrow local RPC casts ────────────────────────────────────────

export interface TrainingProviderAdminRow {
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_key: string;
  display_name: string;
  provider_type: string;
  auth_type: string;
  timestamp_tolerance_seconds: number;
  is_active: boolean;
  secret_configured: boolean;
  public_key_configured: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
  event_count: number | string;
  has_unmatched_events: boolean;
}

export interface TrainingProviderUserMappingAdminRow {
  mapping_id: string;
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  external_user_id: string | null;
  masked_external_user_email: string | null;
  external_user_email_verified: boolean;
  technician_profile_id: string | null;
  technician_display_name: string | null;
  user_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TrainingProviderUnmatchedEventAdminRow {
  event_id: string;
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_event_id: string;
  external_course_id: string | null;
  external_user_id: string | null;
  masked_external_user_email: string | null;
  event_type: string;
  event_status: string;
  processing_status: string;
  reconciliation_status: string | null;
  matched_artifact_id: string | null;
  matched_technician_profile_id: string | null;
  matched_user_id: string | null;
  received_at: string;
  attested_at: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface TechnicianPickerRow {
  id: string;
  name: string;
  technician_profile_id: string | null;
  employment_status: string;
  role: string | null;
  shop_id: string;
}

interface ListProvidersRpc {
  (
    fn: 'list_training_providers_for_admin',
    args: {
      p_org_id: string;
      p_shop_id: string | null;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListUserMappingsRpc {
  (
    fn: 'list_training_provider_user_mappings_for_admin',
    args: {
      p_provider_id: string;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderUserMappingAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListUnmatchedEventsRpc {
  (
    fn: 'list_training_provider_unmatched_events_for_admin',
    args: {
      p_provider_id: string;
      p_status_filter: string[] | null;
      p_limit: number;
      p_offset: number;
    },
  ): Promise<{
    data: TrainingProviderUnmatchedEventAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface UpsertUserMappingRpc {
  (
    fn: 'upsert_training_provider_user_mapping',
    args: {
      p_provider_id: string;
      p_external_user_id: string | null;
      p_external_user_email: string | null;
      p_external_user_email_verified: boolean;
      p_technician_profile_id: string | null;
      p_user_id: string | null;
      p_is_active: boolean;
      p_actor_user_id: string;
      p_reason: string | null;
    },
  ): Promise<{
    data: unknown;
    error: { message: string; code?: string } | null;
  }>;
}

interface ProviderUserMappingAdminPanelProps {
  orgId: string | null | undefined;
  shopId: string | null | undefined;
  currentUserId: string | null | undefined;
}

function safeRpcError(error: { message: string; code?: string } | null): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501') {
    return 'You do not have permission to perform that action.';
  }
  const msg = (error.message ?? '').trim();
  return msg.length === 0 ? 'Action failed.' : msg;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ═══════════════════════════════════════════════════════════════════
// Top-level panel
// ═══════════════════════════════════════════════════════════════════

export function ProviderUserMappingAdminPanel({
  orgId,
  shopId,
  currentUserId,
}: ProviderUserMappingAdminPanelProps) {
  const queryClient = useQueryClient();

  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [includeInactiveMappings, setIncludeInactiveMappings] = useState(false);
  const [externalUserSearch, setExternalUserSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [technicianSearch, setTechnicianSearch] = useState('');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState<
    'all' | 'verified' | 'unverified'
  >('all');

  const enabled = Boolean(orgId);

  // ── Provider list ────────────────────────────────────────────────
  const providersQuery = useQuery({
    queryKey: [
      'training-providers-admin',
      orgId ?? null,
      shopId ?? null,
      includeInactiveProviders,
    ],
    enabled,
    queryFn: async (): Promise<TrainingProviderAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListProvidersRpc;
      const { data, error } = await rpc('list_training_providers_for_admin', {
        p_org_id: orgId as string,
        p_shop_id: shopId ?? null,
        p_include_inactive: includeInactiveProviders,
      });
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  const selectedProvider = useMemo<TrainingProviderAdminRow | null>(() => {
    if (!selectedProviderId) return null;
    return (
      providersQuery.data?.find((p) => p.provider_id === selectedProviderId) ??
      null
    );
  }, [providersQuery.data, selectedProviderId]);

  // ── User mapping list ────────────────────────────────────────────
  const mappingsQuery = useQuery({
    queryKey: [
      'training-provider-user-mappings-admin',
      selectedProviderId,
      includeInactiveMappings,
    ],
    enabled: Boolean(selectedProviderId),
    queryFn: async (): Promise<TrainingProviderUserMappingAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListUserMappingsRpc;
      const { data, error } = await rpc(
        'list_training_provider_user_mappings_for_admin',
        {
          p_provider_id: selectedProviderId as string,
          p_include_inactive: includeInactiveMappings,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  // ── Unmatched events summary ────────────────────────────────────
  const unmatchedQuery = useQuery({
    queryKey: [
      'training-provider-user-unmatched-events-summary',
      selectedProviderId,
    ],
    enabled: Boolean(selectedProviderId),
    queryFn: async (): Promise<TrainingProviderUnmatchedEventAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListUnmatchedEventsRpc;
      const { data, error } = await rpc(
        'list_training_provider_unmatched_events_for_admin',
        {
          p_provider_id: selectedProviderId as string,
          p_status_filter: null,
          p_limit: 100,
          p_offset: 0,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  // ── Technician picker (shop-scoped). Narrow local read of the
  //    safe `technicians` table; never a provider table.
  const techniciansQuery = useQuery({
    queryKey: ['provider-user-mapping-technicians', shopId ?? null],
    enabled: Boolean(shopId),
    queryFn: async (): Promise<TechnicianPickerRow[]> => {
      const { data, error } = await supabase
        .from('technicians')
        .select(
          'id, name, technician_profile_id, employment_status, role, shop_id',
        )
        .eq('shop_id', shopId as string)
        .order('name', { ascending: true });
      if (error) throw new Error(safeRpcError(error));
      return (data ?? []) as TechnicianPickerRow[];
    },
  });

  const refetchMappings = () => {
    queryClient.invalidateQueries({
      queryKey: [
        'training-provider-user-mappings-admin',
        selectedProviderId,
        includeInactiveMappings,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'training-providers-admin',
        orgId ?? null,
        shopId ?? null,
        includeInactiveProviders,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'training-provider-user-unmatched-events-summary',
        selectedProviderId,
      ],
    });
  };

  // ── Dialog state ─────────────────────────────────────────────────
  const [dialogState, setDialogState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; mapping: TrainingProviderUserMappingAdminRow }
    | null
  >(null);
  const [formExternalUserId, setFormExternalUserId] = useState('');
  const [formExternalEmail, setFormExternalEmail] = useState('');
  const [formEmailVerified, setFormEmailVerified] = useState(false);
  const [formTechnicianProfileId, setFormTechnicianProfileId] = useState('');
  const [formAuthUserId, setFormAuthUserId] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formReason, setFormReason] = useState('');
  const [formTechSearch, setFormTechSearch] = useState('');
  const [originalTechnicianProfileId, setOriginalTechnicianProfileId] =
    useState('');
  const [originalAuthUserId, setOriginalAuthUserId] = useState('');
  const [originalIsActive, setOriginalIsActive] = useState(true);
  const [originalEmailVerified, setOriginalEmailVerified] = useState(false);
  const [originalExternalUserId, setOriginalExternalUserId] = useState('');

  useEffect(() => {
    if (!dialogState) return;
    if (dialogState.mode === 'create') {
      setFormExternalUserId('');
      setFormExternalEmail('');
      setFormEmailVerified(false);
      setFormTechnicianProfileId('');
      setFormAuthUserId('');
      setFormIsActive(true);
      setFormReason('');
      setFormTechSearch('');
      setOriginalTechnicianProfileId('');
      setOriginalAuthUserId('');
      setOriginalIsActive(true);
      setOriginalEmailVerified(false);
      setOriginalExternalUserId('');
    } else {
      const m = dialogState.mapping;
      setFormExternalUserId(m.external_user_id ?? '');
      // Raw external email is NOT returned by the J3 read RPC — only
      // masked. Leave the email input blank in edit mode; operator
      // can intentionally re-enter to change the bound email.
      setFormExternalEmail('');
      setFormEmailVerified(m.external_user_email_verified);
      setFormTechnicianProfileId(m.technician_profile_id ?? '');
      setFormAuthUserId(m.user_id ?? '');
      setFormIsActive(m.is_active);
      setFormReason('');
      setFormTechSearch('');
      setOriginalTechnicianProfileId(m.technician_profile_id ?? '');
      setOriginalAuthUserId(m.user_id ?? '');
      setOriginalIsActive(m.is_active);
      setOriginalEmailVerified(m.external_user_email_verified);
      setOriginalExternalUserId(m.external_user_id ?? '');
    }
  }, [dialogState]);

  const upsertMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProviderId) throw new Error('Select a provider first.');
      if (!currentUserId) throw new Error('Sign in required.');
      const extId = formExternalUserId.trim();
      const extEmail = formExternalEmail.trim();
      if (extId.length === 0 && extEmail.length === 0) {
        throw new Error(
          'Provide at least one external identifier (external user ID or email).',
        );
      }
      const techId = formTechnicianProfileId.trim();
      const authId = formAuthUserId.trim();
      if (techId.length === 0 && authId.length === 0) {
        throw new Error(
          'Provide at least one internal identifier (OptiBay technician or auth user).',
        );
      }
      if (authId.length > 0 && !UUID_RE.test(authId)) {
        throw new Error('OptiBay auth user ID must be a valid UUID.');
      }
      const rpc = supabase.rpc.bind(supabase) as unknown as UpsertUserMappingRpc;
      const { error } = await rpc('upsert_training_provider_user_mapping', {
        p_provider_id: selectedProviderId,
        p_external_user_id: extId || null,
        p_external_user_email: extEmail || null,
        p_external_user_email_verified: formEmailVerified,
        p_technician_profile_id: techId || null,
        p_user_id: authId || null,
        p_is_active: formIsActive,
        p_actor_user_id: currentUserId,
        p_reason: formReason.trim() || null,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('User mapping saved.');
      refetchMappings();
      setDialogState(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // ── Reason-required logic for edits ──────────────────────────────
  const isEdit = dialogState?.mode === 'edit';
  const technicianChanged =
    isEdit &&
    formTechnicianProfileId.trim() !== originalTechnicianProfileId.trim();
  const authUserChanged =
    isEdit && formAuthUserId.trim() !== originalAuthUserId.trim();
  const deactivating = isEdit && originalIsActive && !formIsActive;
  const verifiedRaised =
    isEdit && !originalEmailVerified && formEmailVerified;
  const externalUserChanged =
    isEdit && formExternalUserId.trim() !== originalExternalUserId.trim();
  const reasonRequired =
    technicianChanged ||
    authUserChanged ||
    deactivating ||
    verifiedRaised ||
    externalUserChanged;

  // ── Filtered mappings ────────────────────────────────────────────
  const visibleMappings = useMemo(() => {
    const rows = mappingsQuery.data ?? [];
    const ext = externalUserSearch.trim().toLowerCase();
    const em = emailSearch.trim().toLowerCase();
    const tech = technicianSearch.trim().toLowerCase();
    return rows.filter((m) => {
      if (emailVerifiedFilter === 'verified' && !m.external_user_email_verified) {
        return false;
      }
      if (
        emailVerifiedFilter === 'unverified' &&
        m.external_user_email_verified
      ) {
        return false;
      }
      if (
        ext &&
        !(m.external_user_id ?? '').toLowerCase().includes(ext)
      ) {
        return false;
      }
      if (
        em &&
        !(m.masked_external_user_email ?? '').toLowerCase().includes(em)
      ) {
        return false;
      }
      if (
        tech &&
        !(m.technician_display_name ?? '').toLowerCase().includes(tech)
      ) {
        return false;
      }
      return true;
    });
  }, [
    mappingsQuery.data,
    externalUserSearch,
    emailSearch,
    technicianSearch,
    emailVerifiedFilter,
  ]);

  // ── Unmatched-events summary derivations ────────────────────────
  const unmatchedSummary = useMemo(() => {
    const rows = unmatchedQuery.data ?? [];
    const mappedUserIds = new Set(
      (mappingsQuery.data ?? [])
        .map((m) => m.external_user_id)
        .filter((v): v is string => v != null),
    );
    const byUser = new Map<string, { count: number; lastSeen: string | null }>();
    let totalUnmatched = 0;
    for (const e of rows) {
      if (e.matched_user_id != null) continue;
      totalUnmatched += 1;
      const key = e.external_user_id;
      if (!key) continue;
      const prev = byUser.get(key);
      if (prev) {
        prev.count += 1;
        if (!prev.lastSeen || e.received_at > prev.lastSeen) {
          prev.lastSeen = e.received_at;
        }
      } else {
        byUser.set(key, { count: 1, lastSeen: e.received_at });
      }
    }
    const topUsers = Array.from(byUser.entries())
      .filter(([id]) => !mappedUserIds.has(id))
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([id, info]) => ({ external_user_id: id, count: info.count }));
    const lastReceived = rows.reduce<string | null>((acc, e) => {
      if (!acc) return e.received_at;
      return e.received_at > acc ? e.received_at : acc;
    }, null);
    return { totalUnmatched, topUsers, lastReceived };
  }, [unmatchedQuery.data, mappingsQuery.data]);

  const hasStaleMappings = unmatchedSummary.topUsers.length > 0;

  const providerOptions = providersQuery.data ?? [];
  const technicians = techniciansQuery.data ?? [];

  // ── Render ───────────────────────────────────────────────────────

  if (!enabled) {
    return (
      <Card>
        <CardContent
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="provider-user-mapping-admin-panel"
        >
          You do not have access to provider user mapping admin.
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className="space-y-4"
      data-testid="provider-user-mapping-admin-panel"
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Provider User Mapping</CardTitle>
          <CardDescription>
            Bind external provider user IDs / emails to OptiBay technician
            profiles / users. User mapping is identity routing — it does not
            verify training completion or create provider attestation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label
                htmlFor="provider-user-mapping-provider-select"
                className="text-xs"
              >
                Provider
              </Label>
              <select
                id="provider-user-mapping-provider-select"
                data-testid="provider-user-mapping-provider-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedProviderId ?? ''}
                onChange={(e) =>
                  setSelectedProviderId(e.target.value || null)
                }
              >
                <option value="">Select a provider…</option>
                {providerOptions.map((p) => (
                  <option
                    key={p.provider_id}
                    value={p.provider_id}
                    data-testid={`provider-user-mapping-provider-option-${p.provider_id}`}
                  >
                    {p.display_name} — {p.provider_key} ({p.auth_type})
                    {!p.is_active ? ' [inactive]' : ''}
                    {p.has_unmatched_events ? ' [unmatched events]' : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                data-testid="provider-user-mapping-include-inactive-providers"
                checked={includeInactiveProviders}
                onCheckedChange={(v) =>
                  setIncludeInactiveProviders(Boolean(v))
                }
              />
              Include inactive providers
            </label>
          </div>

          {providersQuery.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading providers…
            </div>
          )}
          {providersQuery.error && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="provider-user-mapping-provider-error"
            >
              {/permission|42501/i.test(
                (providersQuery.error as Error).message,
              )
                ? 'You do not have access to provider user mapping.'
                : 'Unable to load providers right now.'}
            </div>
          )}
          {!providersQuery.isLoading &&
            !providersQuery.error &&
            providerOptions.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No providers configured yet. Use the Training Providers tab to
                seed a provider first.
              </div>
            )}
        </CardContent>
      </Card>

      {!selectedProviderId ? (
        <Card>
          <CardContent
            className="py-12 text-center text-sm text-muted-foreground"
            data-testid="provider-user-mapping-no-provider-selected"
          >
            Select a provider to view user mappings.
          </CardContent>
        </Card>
      ) : (
        <>
          {selectedProvider && !selectedProvider.is_active && (
            <div
              className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2"
              data-testid="provider-user-mapping-inactive-provider-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                This provider is inactive. User mapping edits are allowed, but
                provider callbacks may not be accepted until the provider is
                reactivated.
              </span>
            </div>
          )}

          {hasStaleMappings && (
            <div
              className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2"
              data-testid="provider-user-mapping-stale-mappings-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                Some external user IDs have unmatched events but no user
                mapping. Create or update mappings below to route future
                events.
              </span>
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base">User mappings</CardTitle>
                <Button
                  size="sm"
                  data-testid="provider-user-mapping-create-action"
                  onClick={() => setDialogState({ mode: 'create' })}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add user mapping
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-user-mapping-search-external-user"
                    placeholder="Search external user ID…"
                    value={externalUserSearch}
                    onChange={(e) => setExternalUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-user-mapping-search-masked-email"
                    placeholder="Search masked email…"
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-user-mapping-search-technician"
                    placeholder="Search technician…"
                    value={technicianSearch}
                    onChange={(e) => setTechnicianSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  data-testid="provider-user-mapping-email-verified-filter"
                  className="h-10 rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={emailVerifiedFilter}
                  onChange={(e) =>
                    setEmailVerifiedFilter(
                      e.target.value as 'all' | 'verified' | 'unverified',
                    )
                  }
                >
                  <option value="all">Email verified: all</option>
                  <option value="verified">Email verified: yes</option>
                  <option value="unverified">Email verified: no</option>
                </select>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    data-testid="provider-user-mapping-include-inactive-mappings"
                    checked={includeInactiveMappings}
                    onCheckedChange={(v) =>
                      setIncludeInactiveMappings(Boolean(v))
                    }
                  />
                  Include inactive mappings
                </label>
              </div>

              {mappingsQuery.isLoading && (
                <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading user mappings…
                </div>
              )}

              {mappingsQuery.error && (
                <div
                  className="py-8 text-center text-sm text-muted-foreground"
                  data-testid="provider-user-mapping-list-error"
                >
                  {/permission|42501/i.test(
                    (mappingsQuery.error as Error).message,
                  )
                    ? 'You do not have access to provider user mapping.'
                    : 'Unable to load mappings right now.'}
                </div>
              )}

              {!mappingsQuery.isLoading &&
                !mappingsQuery.error &&
                visibleMappings.length === 0 && (
                  <div
                    className="py-8 text-center text-sm text-muted-foreground"
                    data-testid="provider-user-mapping-empty"
                  >
                    No user mappings yet for this provider.
                  </div>
                )}

              {visibleMappings.length > 0 && (
                <ul
                  data-testid="provider-user-mapping-list"
                  className="space-y-2"
                >
                  {visibleMappings.map((m) => (
                    <li
                      key={m.mapping_id}
                      data-testid="provider-user-mapping-row"
                      className="rounded-lg border bg-card p-3 flex flex-wrap items-start justify-between gap-3"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <code
                            data-testid="provider-user-mapping-external-user"
                            className="font-mono text-sm"
                          >
                            {m.external_user_id ?? '—'}
                          </code>
                          <code
                            data-testid="provider-user-mapping-email"
                            className="font-mono text-xs text-muted-foreground"
                            title="Masked provider email"
                          >
                            {m.masked_external_user_email ?? '—'}
                          </code>
                          <span className="sr-only">Masked provider email</span>
                          <Badge
                            variant={
                              m.external_user_email_verified
                                ? 'default'
                                : 'outline'
                            }
                            data-testid="provider-user-mapping-email-verified"
                            className="text-xs"
                          >
                            {m.external_user_email_verified
                              ? 'Email verified'
                              : 'Email unverified'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">→</span>
                          <span
                            data-testid="provider-user-mapping-technician"
                            className="text-sm font-medium"
                          >
                            {m.technician_display_name ??
                              m.technician_profile_id ??
                              '—'}
                          </span>
                          {m.technician_display_name && (
                            <span className="text-xs text-muted-foreground">
                              OptiBay technician
                            </span>
                          )}
                          {m.user_id && (
                            <code
                              data-testid="provider-user-mapping-auth-user"
                              className="font-mono text-xs text-muted-foreground"
                            >
                              OptiBay user {m.user_id}
                            </code>
                          )}
                          {!m.user_id && (
                            <span
                              data-testid="provider-user-mapping-auth-user"
                              className="text-xs text-muted-foreground"
                            >
                              OptiBay user: —
                            </span>
                          )}
                          <Badge
                            variant={m.is_active ? 'default' : 'outline'}
                            data-testid="provider-user-mapping-status"
                            className="text-xs"
                          >
                            {m.is_active ? 'Mapping active' : 'Mapping inactive'}
                          </Badge>
                        </div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                          <div className="flex gap-2">
                            <dt>Created at:</dt>
                            <dd>{m.created_at}</dd>
                          </div>
                          <div className="flex gap-2">
                            <dt>Updated at:</dt>
                            <dd>{m.updated_at}</dd>
                          </div>
                        </dl>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="provider-user-mapping-edit-action"
                          onClick={() =>
                            setDialogState({ mode: 'edit', mapping: m })
                          }
                        >
                          <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {unmatchedQuery.data && unmatchedQuery.data.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Unmatched events</CardTitle>
                <CardDescription>
                  Provider events that have not yet matched an OptiBay
                  technician/user. Click an external user ID to prefill a new
                  user mapping.
                </CardDescription>
              </CardHeader>
              <CardContent
                className="space-y-2 text-sm"
                data-testid="provider-user-mapping-unmatched-events-summary"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <span>
                    Total unmatched:{' '}
                    <strong>{unmatchedSummary.totalUnmatched}</strong>
                  </span>
                  {unmatchedSummary.lastReceived && (
                    <span className="text-xs text-muted-foreground">
                      Last received: {unmatchedSummary.lastReceived}
                    </span>
                  )}
                </div>
                {unmatchedSummary.topUsers.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Suggested from unmatched events:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {unmatchedSummary.topUsers.map((u) => (
                        <Button
                          key={u.external_user_id}
                          size="sm"
                          variant="outline"
                          data-testid="provider-user-mapping-suggested-from-unmatched"
                          onClick={() => {
                            setDialogState({ mode: 'create' });
                            // The dialog reset effect fires first;
                            // defer prefill so we don't get clobbered.
                            setTimeout(() => {
                              setFormExternalUserId(u.external_user_id);
                            }, 0);
                          }}
                        >
                          <span className="font-mono text-xs">
                            {u.external_user_id}
                          </span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {u.count}
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={dialogState !== null}
        onOpenChange={(open) => {
          if (!open) setDialogState(null);
        }}
      >
        <DialogContent data-testid="provider-user-mapping-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialogState?.mode === 'edit'
                ? 'Edit user mapping'
                : 'Add user mapping'}
            </DialogTitle>
            <DialogDescription>
              Bind a provider external user ID / email to an OptiBay
              technician or auth user. Mapping is identity routing — it does
              not create attestation or technician completion.
            </DialogDescription>
          </DialogHeader>

          {selectedProvider && (
            <div className="rounded-md border bg-muted/30 p-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">Provider:</span>
                <span className="font-medium">
                  {selectedProvider.display_name}
                </span>
                <code className="font-mono text-muted-foreground">
                  {selectedProvider.provider_key}
                </code>
                <Badge variant="secondary" className="text-xs">
                  {selectedProvider.auth_type}
                </Badge>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="provider-user-mapping-external-user-id-input">
              External provider user ID
            </Label>
            <Input
              id="provider-user-mapping-external-user-id-input"
              data-testid="provider-user-mapping-external-user-id-input"
              placeholder="provider-user-123"
              value={formExternalUserId}
              onChange={(e) => setFormExternalUserId(e.target.value)}
              autoComplete="off"
            />
            {dialogState?.mode === 'edit' &&
              formExternalUserId.trim() !== originalExternalUserId && (
                <p className="text-xs text-amber-600">
                  Changing the external user ID rebinds this mapping to a
                  different provider learner.
                </p>
              )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="provider-user-mapping-external-email-input">
              External provider email (raw)
            </Label>
            <Input
              id="provider-user-mapping-external-email-input"
              data-testid="provider-user-mapping-external-email-input"
              placeholder="user@provider.example"
              value={formExternalEmail}
              onChange={(e) => setFormExternalEmail(e.target.value)}
              autoComplete="off"
              type="email"
            />
            {dialogState?.mode === 'edit' && (
              <p className="text-xs text-muted-foreground">
                The list shows only the masked email returned by the read RPC.
                Re-enter the email here only if you intend to rebind it.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Provide at least one external identifier (external user ID or
              email).
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="provider-user-mapping-email-verified-toggle"
              checked={formEmailVerified}
              onCheckedChange={(v) => setFormEmailVerified(Boolean(v))}
            />
            {formEmailVerified ? 'Email verified' : 'Email unverified'}
          </label>
          {!formEmailVerified && (
            <p className="text-xs text-amber-600">
              Email unverified — this mapping is lower-trust and will not be
              used by email-based identity fallback.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="provider-user-mapping-technician-search">
              OptiBay technician
            </Label>
            <Input
              id="provider-user-mapping-technician-search"
              data-testid="provider-user-mapping-technician-search"
              placeholder="Search technicians by name…"
              value={formTechSearch}
              onChange={(e) => setFormTechSearch(e.target.value)}
              autoComplete="off"
            />
            <div className="max-h-44 overflow-auto rounded-md border">
              <label
                className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-accent"
                data-testid="provider-user-mapping-technician-option-none"
              >
                <input
                  type="radio"
                  name="provider-user-mapping-technician-id"
                  value=""
                  checked={formTechnicianProfileId === ''}
                  onChange={() => setFormTechnicianProfileId('')}
                />
                <span className="flex-1 text-muted-foreground">
                  No OptiBay technician (use auth user only)
                </span>
              </label>
              {technicians
                .filter((t) => {
                  const q = formTechSearch.trim().toLowerCase();
                  if (!q) return true;
                  return t.name.toLowerCase().includes(q);
                })
                .slice(0, 50)
                .map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-accent"
                    data-testid={`provider-user-mapping-technician-option-${t.id}`}
                  >
                    <input
                      type="radio"
                      name="provider-user-mapping-technician-id"
                      value={t.technician_profile_id ?? ''}
                      checked={
                        !!t.technician_profile_id &&
                        formTechnicianProfileId === t.technician_profile_id
                      }
                      disabled={!t.technician_profile_id}
                      onChange={() =>
                        setFormTechnicianProfileId(
                          t.technician_profile_id ?? '',
                        )
                      }
                    />
                    <span className="flex-1 truncate">{t.name}</span>
                    {t.role && (
                      <Badge variant="outline" className="text-xs">
                        {t.role}
                      </Badge>
                    )}
                    {t.employment_status &&
                      t.employment_status !== 'active' && (
                        <Badge variant="outline" className="text-xs">
                          {t.employment_status}
                        </Badge>
                      )}
                    {!t.technician_profile_id && (
                      <span className="text-xs text-muted-foreground">
                        no profile
                      </span>
                    )}
                  </label>
                ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="provider-user-mapping-auth-user-search">
              OptiBay auth user ID (optional)
            </Label>
            <Input
              id="provider-user-mapping-auth-user-search"
              data-testid="provider-user-mapping-auth-user-search"
              placeholder="UUID — leave blank if not applicable"
              value={formAuthUserId}
              onChange={(e) => setFormAuthUserId(e.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Provide at least one internal identifier (OptiBay technician or
              auth user).
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="provider-user-mapping-active-toggle"
              checked={formIsActive}
              onCheckedChange={(v) => setFormIsActive(Boolean(v))}
            />
            {formIsActive ? 'Mapping active' : 'Mapping inactive'}
          </label>

          <div className="space-y-2">
            <Label htmlFor="provider-user-mapping-reason">
              Reason
              {reasonRequired && <span className="text-destructive"> *</span>}
            </Label>
            <Textarea
              id="provider-user-mapping-reason"
              data-testid="provider-user-mapping-reason"
              placeholder="Operational note (visible in the admin audit log)"
              value={formReason}
              onChange={(e) => setFormReason(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              data-testid="provider-user-mapping-confirm"
              disabled={
                upsertMutation.isPending ||
                (formExternalUserId.trim().length === 0 &&
                  formExternalEmail.trim().length === 0) ||
                (formTechnicianProfileId.trim().length === 0 &&
                  formAuthUserId.trim().length === 0) ||
                (reasonRequired && formReason.trim().length === 0)
              }
              onClick={() => upsertMutation.mutate()}
            >
              {upsertMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              {dialogState?.mode === 'edit'
                ? 'Save user mapping'
                : 'Create user mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
